import {
  workspace_v1_ExportConfig,
  workspace_v1_WorkspaceConfig,
} from '@antimatterhq/asl-api-ts-v1';
import { produce } from 'immer';
import { formatDurationString } from '@antimatterhq/ui/utils/format';
import {
  WorkspaceConfigAlertSchemaValues,
  WorkspaceConfigAlertsSchemaValues,
} from '@/zod/schemas/workspaceConfig/AlertSchema';
import { bumpMetadataVersion } from '@/utils/util';

const getDestination = (exportConfig: workspace_v1_ExportConfig) => {
  const { slackConfig, webhookConfig, jiraConfig, serviceNowConfig } =
    exportConfig;

  if (slackConfig?.message) {
    return 'slack';
  }

  if (webhookConfig?.destination) {
    return 'webhook';
  }

  if (jiraConfig?.email) {
    return 'jira';
  }

  if (serviceNowConfig?.server) {
    return 'servicenow';
  }

  return 'none';
};

const getFormValues = (
  exportConfig: workspace_v1_ExportConfig,
  type: 'notable' | 'opal',
): WorkspaceConfigAlertSchemaValues => {
  const { slackConfig, webhookConfig, jiraConfig, serviceNowConfig } =
    exportConfig;
  const defaultSlackOpalMessage =
    'ALERT [{event_time}] {resource_type}/{resource_name}: {summary} ({details})';
  const defaultSlackNotableMessage =
    'NOTABLE severity={severity} created={created} updated={updated} id={notable_id} count={count} rule={rule} state={state} assignee={assignee} observable={observable}: {summary}';

  const defaultJiraOpalMessage =
    '[{event_time}] {resource_type}/{resource_name}: {summary} example ticket title (summary): [2023-03-18 1400] resource_type/resource_name: summary';

  const defaultJiraNotableMessage =
    '[{updated}] {severity}: {summary} example ticket title (summary): [2023-03-18 1400] critical: something notable happened';

  const defaultSlackMessage =
    type === 'opal' ? defaultSlackOpalMessage : defaultSlackNotableMessage;

  const defaultJiraMessage =
    type === 'opal' ? defaultJiraOpalMessage : defaultJiraNotableMessage;

  const destination = getDestination(exportConfig);

  return {
    destination,
    slackChannel: slackConfig?.channel || undefined,
    slackMessage: slackConfig?.message || defaultSlackMessage,
    slackOAuthType: slackConfig?.token?.scope ? 'secret' : 'token',
    slackTokenKey: slackConfig?.token?.key || undefined,
    slackTokenValue: slackConfig?.token?.value || undefined,
    slackTokenScope: slackConfig?.token?.scope || undefined,
    webhookOutputUrlType: webhookConfig?.destination?.scope ? 'secret' : 'url',
    webhookKey: webhookConfig?.destination?.key || undefined,
    webhookScope: webhookConfig?.destination?.scope || undefined,
    webhookUrl: webhookConfig?.destination?.value || undefined,
    jira: {
      tokenType: jiraConfig?.token?.scope ? 'secret' : 'token',
      token: {
        key: jiraConfig?.token?.key || '',
        value: jiraConfig?.token?.value || '',
        scope: jiraConfig?.token?.scope || '',
      },
      email: jiraConfig?.email || '',
      project: jiraConfig?.project || '',
      issueType: jiraConfig?.issueType || 'Task',
      summary: jiraConfig?.summary || defaultJiraMessage,
      server: jiraConfig?.server || '',
    },
    serviceNowConfig: {
      server: serviceNowConfig?.server || '',
      sourceExpr: serviceNowConfig?.sourceExpr || '',
      nodeExpr: serviceNowConfig?.nodeExpr || '',
      severityExpr: serviceNowConfig?.severityExpr || '',
      eventType: serviceNowConfig?.eventType || '',
      userID: serviceNowConfig?.userID || '',
      password: {
        value: serviceNowConfig?.password?.value || '',
        key: serviceNowConfig?.password?.key || '',
        scope: serviceNowConfig?.password?.scope || '',
      },
      passwordType: serviceNowConfig?.password?.scope ? 'secret' : 'literal',
    },
  };
};

export const getExportAlertFormValues = (
  config?: workspace_v1_WorkspaceConfig,
): WorkspaceConfigAlertsSchemaValues => {
  const { value, unit } = formatDurationString(
    config?.spec?.exportSchedule?.atLeastEvery,
  ) || { value: 10, unit: 'm' };

  const continuous = config?.spec?.exportSchedule?.continuous;
  const cron = config?.spec?.exportSchedule?.exactly || '';
  const schedule = continuous ? 'continuous' : cron ? 'cron' : 'duration';

  const operational = config?.spec.operationalAlertExport;
  const notables = config?.spec.notableExport;
  return {
    notables: getFormValues(notables || {}, 'notable'),
    operational: getFormValues(operational || {}, 'opal'),
    schedule,
    duration: schedule === 'duration' ? Number(value || 10) : undefined,
    durationUnit: schedule === 'duration' ? unit : 'm',
    cron,
  };
};

const setFormValues = (
  values: WorkspaceConfigAlertSchemaValues,
): workspace_v1_ExportConfig | undefined => {
  const {
    destination,
    slackChannel,
    slackMessage,
    slackOAuthType,
    slackTokenKey,
    slackTokenValue,
    slackTokenScope,
    webhookOutputUrlType,
    webhookKey,
    webhookScope,
    webhookUrl,
    serviceNowConfig,
  } = values;
  if (destination === 'none') {
    return undefined;
  }

  const config: workspace_v1_ExportConfig = {
    destination,
    slackConfig:
      destination === 'slack'
        ? {
            channel: slackChannel,
            message: slackMessage,
            token: {
              scope: slackOAuthType === 'secret' ? slackTokenScope : undefined,
              key: slackOAuthType === 'secret' ? slackTokenKey : undefined,
              value: slackOAuthType === 'token' ? slackTokenValue : undefined,
            },
          }
        : undefined,
    webhookConfig:
      destination === 'webhook'
        ? {
            destination: {
              scope:
                webhookOutputUrlType === 'secret' ? webhookScope : undefined,
              key: webhookOutputUrlType === 'secret' ? webhookKey : undefined,
              value: webhookOutputUrlType === 'url' ? webhookUrl : undefined,
            },
          }
        : undefined,
    jiraConfig:
      destination === 'jira'
        ? {
            email: values.jira.email,
            project: values.jira.project,
            issueType: values.jira.issueType,
            summary: values.jira.summary,
            server: values.jira.server,
            token: {
              scope:
                values.jira.tokenType === 'secret'
                  ? values.jira.token.scope
                  : undefined,
              key:
                values.jira.tokenType === 'secret'
                  ? values.jira.token.key
                  : undefined,
              value:
                values.jira.tokenType === 'token'
                  ? values.jira.token.value
                  : undefined,
            },
          }
        : undefined,
    serviceNowConfig:
      destination === 'servicenow'
        ? {
            server: serviceNowConfig?.server,
            sourceExpr: serviceNowConfig?.sourceExpr || undefined,
            nodeExpr: serviceNowConfig?.nodeExpr || undefined,
            severityExpr: serviceNowConfig?.severityExpr || undefined,
            eventType: serviceNowConfig?.eventType || undefined,
            userID: serviceNowConfig?.userID,
            password: {
              value:
                serviceNowConfig?.passwordType === 'literal'
                  ? serviceNowConfig?.password?.value || undefined
                  : undefined,
              key:
                serviceNowConfig?.passwordType === 'secret'
                  ? serviceNowConfig?.password?.key || undefined
                  : undefined,
              scope:
                serviceNowConfig?.passwordType === 'secret'
                  ? serviceNowConfig?.password?.scope || undefined
                  : undefined,
            },
          }
        : undefined,
  };

  return config;
};

export const setExportAlertFormValues = (
  config: workspace_v1_WorkspaceConfig,
  values: WorkspaceConfigAlertsSchemaValues,
): workspace_v1_WorkspaceConfig => {
  const result = produce(config, (draft) => {
    draft.spec.notableExport = setFormValues(values.notables);
    draft.spec.operationalAlertExport = setFormValues(values.operational);

    if (
      values.notables.destination !== 'none' ||
      values.operational.destination !== 'none'
    ) {
      switch (values.schedule) {
        case 'duration':
          draft.spec.exportSchedule = {
            atLeastEvery: `${values.duration}${values.durationUnit}`,
          };
          break;
        case 'cron':
          draft.spec.exportSchedule = {
            exactly: values.cron,
          };
          break;
        case 'continuous':
          draft.spec.exportSchedule = {
            continuous: true,
          };
          break;
      }
    } else {
      draft.spec.exportSchedule = undefined;
    }

    bumpMetadataVersion(draft);
  });

  return result;
};
